import { DateTime } from 'luxon'

import { PROGRAM_SLUG_TO_PROGRAM_ITERATIONS } from './config'
import type { ProgramIteration, ProgramOfferingTemplateWithDates } from './types'

// The following helpers will be replaced with query builder queries in the future, such as ProgramIterationQ.getActiveProgramIterations. These functions are called from the hooks for fetching program iterations such as `useActiveProgramIterations`, and used rarely elsewhere. Please use sparingly, as they only fetch non-SAT programs.

type SupportedProgramSlug = keyof typeof PROGRAM_SLUG_TO_PROGRAM_ITERATIONS

// Program iterations where learner registration has opened but the series have not ended. This can include programs where the learner registration deadline has passed.
export const getActiveProgramIterations = (
  programSlug: SupportedProgramSlug
): ProgramIteration[] => {
  const programIterations = PROGRAM_SLUG_TO_PROGRAM_ITERATIONS[programSlug]
  return programIterations.filter(
    iteration =>
      iteration.learnerRegistrationOpens < DateTime.now() && iteration.endDate > DateTime.now()
  )
}

export const getProgramIterationOpenForRegistration = (
  programSlug: SupportedProgramSlug,
  as: 'tutor' | 'learner'
): ProgramIteration | null => {
  const programIterations = PROGRAM_SLUG_TO_PROGRAM_ITERATIONS[programSlug]
  return (
    programIterations.find(iteration => {
      const deadline =
        as === 'tutor' ? iteration.tutorRegistrationDeadline : iteration.learnerRegistrationDeadline
      const releasedAt =
        as === 'tutor' ? iteration.tutorRegistrationOpens : iteration.learnerRegistrationOpens

      return deadline > DateTime.now() && releasedAt < DateTime.now()
    }) ?? null
  )
}

export const getNextProgramIterationOpenForRegistration = ({
  programSlug,
  type,
  excludeCurrentlyOpen = false,
}: {
  programSlug: SupportedProgramSlug
  type: 'learner' | 'tutor'
  excludeCurrentlyOpen?: boolean
}): ProgramIteration | null => {
  const programIterations = PROGRAM_SLUG_TO_PROGRAM_ITERATIONS[programSlug]
  return (
    programIterations.find(iteration => {
      const deadline =
        type === 'tutor'
          ? iteration.tutorRegistrationDeadline
          : iteration.learnerRegistrationDeadline
      const registrationOpens =
        type === 'tutor' ? iteration.tutorRegistrationOpens : iteration.learnerRegistrationOpens
      return (
        deadline > DateTime.now() &&
        (excludeCurrentlyOpen ? registrationOpens > DateTime.now() : true)
      )
    }) ?? null
  )
}

export const getNextUnreleasedProgramIteration = ({
  programSlug,
  type,
}: {
  programSlug: SupportedProgramSlug
  type: 'learner' | 'tutor'
}): ProgramIteration | null => {
  const programIterations = PROGRAM_SLUG_TO_PROGRAM_ITERATIONS[programSlug]
  return (
    programIterations.find(iteration => {
      const registrationOpens =
        type === 'tutor' ? iteration.tutorRegistrationOpens : iteration.learnerRegistrationOpens
      return registrationOpens > DateTime.now()
    }) ?? null
  )
}

export const getUpcomingIterations = ({
  programSlug,
  maxIterations,
  type,
}: {
  programSlug: SupportedProgramSlug
  maxIterations?: number
  type: 'learner' | 'tutor'
}): ProgramIteration[] => {
  const programIterations = PROGRAM_SLUG_TO_PROGRAM_ITERATIONS[programSlug]
  const now = DateTime.now()
  const futureIterations = programIterations
    .filter(iteration =>
      type === 'learner'
        ? iteration.learnerRegistrationDeadline > now
        : iteration.tutorRegistrationDeadline > now
    )
    .slice(0, maxIterations != null ? maxIterations : undefined)

  return futureIterations
}

export const getUpcomingOfferingTemplates = ({
  programSlug,
  type,
  maxIterations = 3,
}: {
  programSlug: SupportedProgramSlug
  type: 'learner' | 'tutor'
  maxIterations?: number
}): ProgramOfferingTemplateWithDates[] => {
  const futureIterations = getUpcomingIterations({ programSlug, maxIterations, type })
  const offeringSlugToTemplateDetails: Record<string, ProgramOfferingTemplateWithDates> = {}
  const now = DateTime.now()

  futureIterations.forEach(iteration => {
    iteration.offerings.forEach(offering => {
      const { offeringSlug } = offering.offeringTemplate
      if (!(offeringSlug in offeringSlugToTemplateDetails)) {
        offeringSlugToTemplateDetails[offeringSlug] = {
          ...offering.offeringTemplate,
          dates: [],
          registrationStatus: null,
        }
      }
      const template = offeringSlugToTemplateDetails[offeringSlug]
      template.dates.push({
        startDate: offering.startDate,
        endDate: offering.endDate,
      })

      if (template.registrationStatus === null) {
        if (
          now >= iteration.learnerRegistrationOpens &&
          now <= iteration.learnerRegistrationDeadline
        ) {
          template.registrationStatus = {
            isOpen: true,
            registrationDeadline: iteration.learnerRegistrationDeadline,
          }
        } else {
          template.registrationStatus = {
            isOpen: false,
            opensAt: iteration.learnerRegistrationOpens,
          }
        }
      }
    })
  })

  return Object.values(offeringSlugToTemplateDetails)
}
